// BI unificata — helper condivisi (formati, delta, nota fonte, controllo segmentato)
import React, { useState } from "react";
import { Info, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import { formatMoney } from "../../utils/currency";

export const useFm = () => {
  const { company, displayMode } = useCompany();
  return (v) => formatMoney(v ?? 0, company, { displayMode });
};
export const fmtKg = (n, d = 1) => (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmtInt = (n) => (n ?? 0).toLocaleString("it-IT");
export const fmtDate = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");

export const Delta = ({ value, suffix = "vs periodo prec.", className = "", invert = false }) => {
  if (value == null) return <span className={`text-[11px] text-stone-400 ${className}`}>— {suffix}</span>;
  const up = value > 0.05, down = value < -0.05;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const good = invert ? down : up, bad = invert ? up : down;
  const tone = good ? "text-emerald-700 bg-emerald-50" : bad ? "text-red-700 bg-red-50" : "text-stone-500 bg-stone-100";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${tone} ${className}`} data-testid="bi-delta">
      <Icon className="w-3 h-3" /> {value > 0 ? "+" : ""}{value.toLocaleString("it-IT", { maximumFractionDigits: 1 })}% <span className="font-normal opacity-70">{suffix}</span>
    </span>
  );
};

export const SourceNote = ({ text, testId = "bi-source-note" }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-9 h-9 -m-2 inline-flex items-center justify-center text-stone-400 hover:text-[#8A1C2A]" aria-label="Da dove deriva questo numero" data-testid={testId}>
        <Info className="w-4 h-4" />
      </button>
      {open && (
        <span className="absolute z-30 right-0 top-6 w-64 text-left text-[11px] leading-snug text-stone-700 bg-white border border-stone-200 shadow-lg rounded-lg p-2.5" onClick={() => setOpen(false)}>
          <b className="text-[#8A1C2A]">Fonte:</b> {text}
        </span>
      )}
    </span>
  );
};

export const Seg = ({ options, value, onChange, testIdPrefix = "seg", size = "md", wrap = false }) => (
  <div className={`inline-flex bg-stone-100 rounded-xl p-1 gap-1 max-w-full ${wrap ? "flex-wrap" : "overflow-x-auto"}`}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        data-testid={`${testIdPrefix}-${o.value}`}
        className={`${size === "sm" ? "h-10 px-3 text-xs" : "h-11 px-4 text-sm"} rounded-lg font-semibold whitespace-nowrap transition-colors ${value === o.value ? "bg-[#8A1C2A] text-white shadow-sm" : "text-stone-600 hover:bg-white"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const Bars = ({ data, valueKey, labelKey = "label", color = "#8A1C2A", formatValue, emptyLabel = "Nessun dato nel periodo", onPick, testIdPrefix = "bi-bar" }) => {
  if (!data?.length) return <p className="text-sm text-stone-500 italic py-8 text-center">{emptyLabel}</p>;
  const max = Math.max(...data.map((d) => d[valueKey] || 0), 1);
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => {
        const v = d[valueKey] || 0;
        const w = Math.max(2, (v / max) * 100);
        const inside = w >= 28;
        const Row = onPick ? "button" : "div";
        return (
          <Row key={d.bucket || d.code || d.key || i} onClick={onPick ? () => onPick(d) : undefined} className={`w-full flex items-center gap-2 text-xs min-h-[36px] ${onPick ? "hover:bg-stone-50 rounded-md text-left" : ""}`} data-testid={`${testIdPrefix}-${d.bucket || d.code || d.key || i}`}>
            <span className="w-24 sm:w-28 text-stone-700 truncate font-medium shrink-0">{d[labelKey]}</span>
            <div className="flex-1 bg-stone-100 rounded-full h-7 relative overflow-hidden flex items-center">
              <div className="h-full rounded-full flex items-center justify-end pr-2 transition-[width] duration-300" style={{ width: `${w}%`, backgroundColor: color }}>
                {inside && <span className="text-[11px] font-semibold text-white whitespace-nowrap">{formatValue(v)}</span>}
              </div>
              {!inside && <span className="ml-2 text-[11px] font-semibold text-stone-800 whitespace-nowrap">{formatValue(v)}</span>}
            </div>
          </Row>
        );
      })}
    </div>
  );
};
